import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import classNames from 'clsx';
import { useTheme } from '@mui/material/styles';
import { CloseRounded, SearchRounded } from '@mui/icons-material';

import { QUERY_SEARCH_KEY } from '../../constants/queryParams';
import { SEARCH_PATH } from '../../constants/routePath';
import { SHAPE_CIRCULAR, SHAPE_SQUARE, SIZE_LARGE, SIZE_X_LARGE, VARIANT_OUTLINED } from '@/mui/constants';

import { parseQueryObject } from '../../../Search/helpers/query';
import * as searchAutocompleteSelectors from '../../../Search/redux/selectors/autocomplete';
import { autocompleteRequestAction } from '../../../Search/redux/slices/autocomplete';
import {
  clearSearchResultAction,
  searchRequestAction,
  setSearchQueryAction,
} from '../../../Search/redux/slices/search';
import { searchAiRequestAction } from '../../../Search/redux/slices/searchAi';
import { getPathNameByParams, processHistoryObject } from '../../helpers/history';
import * as commonSelectors from '../../redux/selectors/common';
import { fixButtonVariant } from '@/modules/common/helpers/configuration';
import { useTranslate } from '@/modules/common/helpers/i18n';
import { getColorByChainString } from '@/mui/helpers/color';

import ConfigurationIcon from '@/modules/common/components/ConfigurationIcon/ConfigurationIcon';
import { Autocomplete, Button, CircularProgress, InputAdornment, TextField } from '@/mui/components';

import en from '../../i18n/en.json';

import styles from './SearchPanel.module.scss';

type Props = {
  value?: string;
};

function SearchPanel({ value = '' }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();

  const configuration = useSelector(commonSelectors.configurationSelector);
  const search = useSelector(commonSelectors.searchSelector);
  const genAI = useSelector(commonSelectors.genAISelector);
  const autocompleteList = useSelector(searchAutocompleteSelectors.autocompleteList);
  const autocompleteListLoaded = useSelector(searchAutocompleteSelectors.autocompleteListLoaded);
  const searchPlaceholder = useSelector(commonSelectors.searchPlaceholderSelector);
  const t = useTranslate();

  const options = autocompleteList.map((item: string) => ({ label: item })) as Array<Record<'label', string>>;

  const [inputValue, setInputValue] = useState(value);
  const [preventSubmit, setSubmitState] = useState(false);

  const onSubmit = (rawValue: string) => {
    inputRef.current?.blur();

    const trimmedValue = rawValue.trim();
    if (!trimmedValue) return;

    const searchQuery = parseQueryObject(location.search) as Record<string, string>;

    const query = {
      ...searchQuery,
      [QUERY_SEARCH_KEY]: trimmedValue,
    };

    if (searchQuery[QUERY_SEARCH_KEY] !== trimmedValue) {
      dispatch(setSearchQueryAction({}));
      navigate(
        processHistoryObject({
          pathname: getPathNameByParams(SEARCH_PATH),
          search: query,
        }),
      );
    } else {
      dispatch(clearSearchResultAction());
      dispatch(searchRequestAction({ query, isAddRequest: false }));
      if (genAI) {
        dispatch(searchAiRequestAction({ query }));
      }
    }
  };

  const onKeyUpFn = (event: React.KeyboardEvent<HTMLInputElement>) => {
    setTimeout(() => {
      if (event.code === 'Enter' && !preventSubmit) {
        onSubmit(inputValue);
      }
      setSubmitState(false);
    }, 0);
  };

  useEffect(() => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      dispatch(autocompleteRequestAction(trimmed));
    }
  }, [inputValue, dispatch]);

  const inputConfig = configuration.common.search.input;
  const searchButtonConfig = configuration.common.search.searchButton;
  const size = inputConfig.size ?? SIZE_X_LARGE;
  const adaptedSize = size === SIZE_X_LARGE ? SIZE_LARGE : size;

  const borderColor = inputConfig.borderColor ? getColorByChainString(theme, inputConfig.borderColor) : undefined;
  const borderColorHover = inputConfig.borderColorHover
    ? getColorByChainString(theme, inputConfig.borderColorHover)
    : undefined;
  const borderColorFocus = inputConfig.borderColorFocus
    ? getColorByChainString(theme, inputConfig.borderColorFocus)
    : undefined;

  let placeholder = inputConfig.placeholderText ?? searchPlaceholder;

  if (placeholder === undefined || placeholder === null || placeholder === en.searchPlaceholder) {
    placeholder = t.searchPlaceholder.text();
  }

  return search ? (
    <Autocomplete
      freeSolo
      size={adaptedSize}
      options={options}
      optionLabelKey="label"
      optionValueKey="label"
      value={inputValue}
      disableClearable
      // initial requirements: option from the list should not be selected at all, it is by design, do not change
      isOptionEqualToValue={() => false}
      onInputChange={(_, value$) => setInputValue(value$)}
      onChange={(_, value$) => {
        setSubmitState(true);
        onSubmit((value$ as Record<'label', string>).label as string);
      }}
      onKeyUp={onKeyUpFn}
      renderInput={(params) => {
        const buttonClassName = classNames(styles.InputButton, {
          [styles.InputButton___xLarge]: size === SIZE_X_LARGE,
        });

        return (
          <TextField
            {...params}
            inputRef={inputRef}
            shape={inputConfig.shape}
            variant={inputConfig.variant ?? 'outlined'}
            size={adaptedSize}
            aria-label={t.searchField.text()}
            placeholder={placeholder}
            style={
              {
                fontFamily: inputConfig.fontFamily,
                '--internal-border-color': borderColor,
                '--internal-border-color-hover': borderColorHover,
                '--internal-border-color-focus': borderColorFocus,
              } as CSSProperties
            }
            InputProps={{
              ...params.InputProps,
              classes: {
                input: classNames(styles.Input, {
                  [styles.Input___xLarge]: size === SIZE_X_LARGE,
                }),
                root: classNames(styles.InputRoot, {
                  [styles.InputRoot___customBorder]: borderColor,
                  [styles.InputRoot___customBorderHover]: borderColorHover,
                }),
                focused: borderColorFocus ? styles.InputRoot___customBorderFocus : undefined,
                notchedOutline: styles.NotchedOutline,
              },
              endAdornment: (
                <InputAdornment position="end">
                  {autocompleteListLoaded && (
                    <Button
                      aria-label={t.loadingResults.text()}
                      type="button"
                      shape="square"
                      variant="text"
                      size={adaptedSize}
                      disableElevation
                      color={searchButtonConfig.color ?? 'neutral'}
                      square
                      className={buttonClassName}
                      startIcon={<CircularProgress size={adaptedSize} color={searchButtonConfig.color ?? undefined} />}
                    />
                  )}
                  {!!inputValue && (
                    <Button
                      aria-label={t.clearButton.text()}
                      type="button"
                      shape="square"
                      variant="text"
                      size={adaptedSize}
                      disableElevation
                      color={searchButtonConfig.color ?? 'neutral'}
                      square
                      className={buttonClassName}
                      onClick={() => setInputValue('')}
                      startIcon={<CloseRounded />}
                    />
                  )}
                  <Button
                    variant={fixButtonVariant(searchButtonConfig.variant, VARIANT_OUTLINED)}
                    aria-label={t.searchButton.text()}
                    type="button"
                    shape="square"
                    disableElevation
                    size={adaptedSize}
                    color={searchButtonConfig.color ?? 'neutral'}
                    square
                    className={classNames(buttonClassName, styles.InputButtonAdapt, {
                      [styles.InputButtonAdapt___square]: inputConfig.shape === SHAPE_SQUARE,
                      [styles.InputButtonAdapt___circular]: inputConfig.shape === SHAPE_CIRCULAR,
                    })}
                    onClick={(event) => {
                      event.stopPropagation();
                      onSubmit(inputValue);
                    }}
                    startIcon={<ConfigurationIcon iconKey={searchButtonConfig.icon} fallback={SearchRounded} />}
                  />
                </InputAdornment>
              ),
            }}
          />
        );
      }}
      aria-label={t.searchWithinVideoField.text()}
    />
  ) : null;
}

export default SearchPanel;
